/**
 * Public REST API client for the marketing site.
 * Replaces Apollo/GraphQL with simple fetch calls to /api/public/ endpoints.
 */

const BASE = '/api/public'

function getHeaders () {
  const headers = { 'Content-Type': 'application/json' }
  const token = process.env.VUE_APP_MARKETING_API_TOKEN
  if (token) {
    headers['X-API-Key'] = token
  }
  return headers
}

async function fetchJson (url) {
  const headers = getHeaders()
  delete headers['Content-Type'] // not needed for GET
  const response = await fetch(url, { credentials: 'include', headers })
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

async function postJson (url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const error = new Error(err.error || `API error: ${response.status}`)
    error.status = response.status
    throw error
  }
  return response.json()
}

/**
 * GET /api/public/libraries
 * Returns: { libraryInfo: { libraries }, contentSlugs, vision: { nodes }, occupational: { nodes } }
 */
export function fetchLibraries () {
  return fetchJson(`${BASE}/libraries`)
}

/**
 * GET /api/public/license-packs?libraryId=
 */
export function fetchLicensePacks (libraryId) {
  const qs = libraryId ? `?libraryId=${encodeURIComponent(libraryId)}` : ''
  return fetchJson(`${BASE}/license-packs${qs}`)
}

/**
 * GET /api/public/games?libraryId=
 * Returns: { allGames, allTherapyCategories, libraryById, allLibraries }
 */
export function fetchGames (libraryId) {
  const qs = libraryId ? `?libraryId=${encodeURIComponent(libraryId)}` : ''
  return fetchJson(`${BASE}/games${qs}`)
}

/**
 * GET /api/public/games/:playKey
 * Returns: { gameByPlayKey, gameContentSlugs, gameContentImages, allLibraries, allTherapyCategories, allCognitiveDomains }
 */
export function fetchGameDetail (playKey) {
  return fetchJson(`${BASE}/games/${encodeURIComponent(playKey)}`)
}

/**
 * POST /api/public/subscribe
 */
export function subscribeFromBrochure (subscribeFromBrochureInput) {
  return postJson(`${BASE}/subscribe`, { subscribeFromBrochureInput })
}

/**
 * POST /api/public/request-demo
 * Creates a demo game session and returns gameplay URL.
 * Returns: { data: { requestGameDemo: { demoRequestResult: { gameSessionId, gameplayUrl, ... } } } }
 */
export function requestDemo (demoRequestInfo) {
  return postJson(`${BASE}/request-demo`, { demoRequestInfo })
}
